import { ModuleDto } from './dtos';
import { estimatedMinutesError } from './estimated-minutes';
import { answerTableSourceIssues } from '../components/content-viewer/fragment-answers';

export type ModuleValidationField = 'name' | 'instructions' | 'description' | 'estimated_minutes' | 'prerequisites' | 'finalQuizFragmentKey';
export type FragmentValidationField = 'title' | 'content' | 'instructions' | 'actions';
export type AuthoringIssue = {
    id: string;
    label: string;
    message: string;
} & ({ scope: 'module'; field: ModuleValidationField | 'fragments' }
    | { scope: 'fragment'; fragmentKey: string; field: FragmentValidationField });

export type ManagedModuleValidationErrors = Partial<Record<ModuleValidationField, string>> & { fragments?: string[] };

/** One rule set drives both the authoring checklist and Save validation. */
export function managedModuleIssues(module: ModuleDto): AuthoringIssue[] {
    const issues: AuthoringIssue[] = [];
    const fragments = module.fragments ?? [];
    const moduleIssue = (field: ModuleValidationField | 'fragments', label: string, message: string) =>
        issues.push({ id: `module:${field}`, scope: 'module', field, label, message });
    const ordered = [...fragments].sort((a, b) => a.order - b.order);
    for (const [index, fragment] of ordered.entries()) {
        const fragmentIssue = (field: FragmentValidationField, label: string, message: string, suffix = '') =>
            issues.push({ id: `fragment:${fragment.key}:${field}${suffix}`, scope: 'fragment', fragmentKey: fragment.key,
                field, label: `Fragment ${index + 1}${fragment.title ? ` (${fragment.title})` : ''}: ${label}`, message });
        const name = fragment.title?.length ? `"${fragment.title}"` : '<unnamed>';
        if (!fragment.title?.trim()) fragmentIssue('title', 'add a title', `Fragment ${name}(${fragment.order}) title is required.`);
        if (!fragment.html || fragment.html === '<p></p>') fragmentIssue('content', 'add content', `Fragment ${name}(${fragment.order}) HTML content is required.`);
        if (!fragment.instructions?.trim()) fragmentIssue('instructions', 'add instructions', `Fragment ${name}(${fragment.order}) instructions are required.`);
        if (!fragment.actions?.length) fragmentIssue('actions', 'add an action', `Fragment ${name}(${fragment.order}) must have at least one action.`);
    }
    answerTableSourceIssues(fragments).forEach((issue, index) => {
        const number = ordered.findIndex(f => f.key === issue.fragmentKey) + 1;
        issues.push({ id: `answer-table:${issue.fragmentKey}:${index}`, scope: 'fragment', fragmentKey: issue.fragmentKey,
            field: 'content', label: `Fragment ${number}: choose an earlier fragment as the answer table source`, message: issue.message });
    });
    if (module.finalQuizFragmentKey && !fragments.some(f => f.key === module.finalQuizFragmentKey))
        moduleIssue('finalQuizFragmentKey', 'Choose a final quiz fragment from this module', 'Final quiz fragment must belong to this module.');
    const durationError = estimatedMinutesError(module.estimated_minutes);
    if (durationError) moduleIssue('estimated_minutes', `Correct the estimated duration: ${durationError}`, durationError);
    if (!module.name?.trim()) moduleIssue('name', 'Add a module name', 'Module name is required.');
    if (!module.instructions?.trim()) moduleIssue('instructions', 'Add module instructions', 'Module instructions are required.');
    if (!module.description?.trim()) moduleIssue('description', 'Add a module description', 'Module description is required.');
    if (module.prerequisites?.some(p => p.module_key === module.module_key))
        moduleIssue('prerequisites', 'Remove this module from its own prerequisites', 'A module cannot depend on itself.');
    if (!fragments.length) moduleIssue('fragments', 'Add a fragment', 'The module must contain at least one fragment.');
    // Keep module settings before fragment corrections, in a stable order.
    return [...issues.filter(issue => issue.scope === 'module'), ...issues.filter(issue => issue.scope === 'fragment')];
}

export function moduleValidationErrors(issues: AuthoringIssue[]): ManagedModuleValidationErrors {
    const errors: ManagedModuleValidationErrors = {};
    issues.forEach(issue => {
        if (issue.scope === 'module' && issue.field !== 'fragments') errors[issue.field] = issue.message;
    });
    return errors;
}
